import { fetch } from 'http-common';

import { Zone } from 'types';
import { ListType } from 'types/api/api.type';
import { User } from 'types/api/users.type';

import { SortItem } from 'components/Table/components/types';

type UsersDataList = Omit<ListType, 'content'> & { content: User[] };
type PostData = {
  [x: string]: any; // eslint-disable-line
};
type ZoneList = Omit<ListType, 'data'> & { data: Zone[] };

class UsersDataService {
  private http = fetch();

  private ep = '/v1/users';

  public async getAll(page?: number, size?: number, params?: URLSearchParams) {
    return this.http.get<UsersDataList>(`${this.ep}/search`, {
      params: {
        page: page || 0,
        size: size || 10,
        email: params?.get('email') || undefined,
        name: params?.get('name') || undefined,
        surname: params?.get('surname') || undefined,
        customerName: params?.get('customerName') || undefined,
        userTypeId: params?.get('userTypeId') || undefined,
      },
    });
  }

  public async getDeletedUsers(
    page?: number,
    size?: number,
    params?: URLSearchParams,
    sorting?: SortItem
  ) {
    return this.http.get<User>(`${this.ep}/deleted`, {
      params: {
        page: page || 0,
        size: size || 10,
        email: params?.get('email') || undefined,
        name: params?.get('name') || undefined,
        surname: params?.get('surname') || undefined,
        customerName: params?.get('customerName') || undefined,
        userTypeId: params?.get('userTypeId') || undefined,
        sort:
          sorting && sorting.id !== 'code'
            ? `${sorting.id === 'createdAt' ? 'timestamp' : sorting.id},${
                sorting.desc ? 'desc' : 'asc'
              }`
            : undefined,
      },
    });
  }

  public async getUser(id: string) {
    return this.http.get<User>(`${this.ep}/${id}`);
  }

  public async putUser(id: string, data: PostData) {
    return this.http.put<User>(`${this.ep}/${id}`, data);
  }

  public async postUser(data: PostData) {
    return this.http.post<User>(`${this.ep}`, { ...data, autoConfirmUser: true });
  }

  public async restoreUser(id: string) {
    return this.http.post(`${this.ep}/${id}/restore`);
  }

  public async getSeasons() {
    return this.http.get(`${this.ep}/seasons`);
  }

  public async addSeason(data: PostData) {
    return this.http.post(`${this.ep}/seasons`, data);
  }

  public async getZones(availableOnly?: boolean) {
    return this.http.get(`${this.ep}/zones${availableOnly ? '?type=' : ''}`);
  }

  public async addZone(data: PostData) {
    return this.http.post(`${this.ep}/zones`, data);
  }

  public async getCurrentUserZones(withSpecialZone: boolean) {
    return this.http.get(`${this.ep}/zones/myZones`, {
      params: { withspecialzone: withSpecialZone },
    });
  }

  public async getPermissions() {
    return this.http.get<ZoneList>(`${this.ep}/permissions`);
  }

  public async getUserTypes() {
    return this.http.get(`${this.ep}/usertypes`);
  }

  public async getPriceList() {
    return this.http.get(`${this.ep}/pricelist`);
  }

  public async getCurrentUser() {
    return this.http.get<User>(`${this.ep}/me`);
  }

  public async delete(id: string) {
    return this.http.delete<User>(`${this.ep}/${id}`);
  }
}

const usersDataService = new UsersDataService();

export { usersDataService };
